import dynamic from 'next/dynamic'

import { mq, spacing, styled, Text } from '@thg-commerce/gravity-theme'
import { HorizontalAlignment } from '@thg-commerce/enterprise-core'
import {
  Button,
  SafeHtml,
  Separator,
  Input,
} from '@thg-commerce/gravity-elements'
import { Grid, GridItem } from '@thg-commerce/gravity-system'

const Remove = dynamic(
  () => import('@thg-commerce/gravity-icons/src/components/Close'),
)
const GravityPlus = dynamic(
  () => import('@thg-commerce/gravity-icons/src/components/Plus'),
)

export const Title = styled.h2<{ alignment?: HorizontalAlignment }>`
  ${Text('large1', 'default')};
  text-align: ${(props) => props.alignment || 'left'};
  margin-bottom: ${spacing(5)};
`

export const CalculatorGrid = styled(Grid)`
${(props) => mq(props.theme.breakpointUtils.map, 'md')} {
  column-gap: 64px;
`

export const Subtitle = styled.p<{ alignment?: HorizontalAlignment }>`
  ${Text('medium1', 'default')};
  padding-bottom: ${spacing(1)};
`

export const StyledSeparator = styled(Separator)`
  margin: ${spacing(2)} 0 ${spacing(2)};
`

export const Label = styled.p`
  ${Text('bodyText', 'alternate')};
  margin: ${spacing(1)} 0;
`

export const AdditionalInfoText = styled(SafeHtml)`
  margin-bottom: ${spacing(3)};

  ${(props) => mq(props.theme.breakpointUtils.map, 'md')} {
    margin-bottom: ${spacing(5.5)};
  }

  p {
    ${Text('small', 'default')};
  }

  b {
    ${Text('small', 'alternate')};
  }
`

export const InstructionsTitle = styled(GridItem)`
  ${Text('medium1', 'default')};
`

export const InstructionsText = styled(SafeHtml)<{
  showInstructionsOnMobile?: boolean
}>`
  margin: ${spacing(2)} 0 ${spacing(2)};
  ${(props) => !props.showInstructionsOnMobile && 'display: none;'}
  ${(props) => mq(props.theme.breakpointUtils.map, 'md')} {
    display: block;
  }

  b {
    ${Text('bodyText', 'alternate')};
  }

  ol {
    list-style: auto;
    padding-left: ${spacing(2.5)};
  }
`

export const RemoveFormItemButton = styled.button`
  ${Text('bodyText', 'default')};
  align-self: center;
  display: flex;
  margin: ${spacing(2)} 0 0;

  ${(props) => mq(props.theme.breakpointUtils.map, 'md')} {
    position: absolute;
    right: ${spacing(-3)};
    margin: 0 0 ${spacing(-4)};
  }
`

export const RemoveText = styled.div`
  margin-left: ${spacing(1)};

  ${(props) => mq(props.theme.breakpointUtils.map, 'md')} {
    display: none;
  }
`

export const RemoveIcon = styled(Remove)`
  background: ${(props) => props.theme.colors.palette.greys.light};
  border-radius: 50%;
  padding: ${spacing(0.4)};

  :hover,
  :focus {
    background: ${(props) =>
      props.theme.formgroup.general.border['focus'].color};
    path {
      fill: ${(props) => props.theme.colors.palette.greys.white};
    }
  }
`

export const GravityPlusIcon = styled(GravityPlus)<{ color?: string }>`
  path {
    fill: ${(props) =>
      !props.color && props.theme.formgroup.general.border['focus'].color};
  }
`

export const FieldsContainer = styled.div`
  margin-bottom: ${spacing(2)};
  position: relative;

  ${(props) => mq(props.theme.breakpointUtils.map, 'sm')} {
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
    flex-direction: row;
  }
`

export const CalculatorInfoContainer = styled.div`
  background: ${(props) => props.theme.colors.palette.greys.light};
  padding: ${spacing(3)};
  margin-bottom: ${spacing(1)};

  ${(props) => mq(props.theme.breakpointUtils.map, 'md')} {
    padding: ${spacing(3)} ${spacing(4)};
  }
`

export const InfoContainer = styled.div`
  background: ${(props) => props.theme.colors.palette.greys.light};
  padding: ${spacing(3)};
  margin-bottom: ${spacing(4)};

  ${(props) => mq(props.theme.breakpointUtils.map, 'md')} {
    padding: ${spacing(3)} ${spacing(4)};
  }

  div {
    padding: 0;
    height: auto;

    label div {
      ${Text('bodyText', 'default')};
    }

    :hover,
    :focus,
    :active {
      background: transparent;
    }
  }
`

export const ExtraCoverageText = styled.p`
  ${Text('small', 'default')};
  margin-top: ${spacing(2)};
`

export const StyledButton = styled(Button)`
  width: 100%;
`

export const AddButton = styled(Button)`
  margin-top: ${spacing(1)};
`

export const ResultsTitle = styled.div`
  ${Text('large1', 'alternate')};
  margin-bottom: ${spacing(2)};
`

export const SmallText = styled.p`
  ${Text('small', 'default')};
  margin-bottom: ${spacing(2)};
`

export const MediumText = styled.p`
  ${Text('bodyText', 'alternate')};
  margin-bottom: ${spacing(2)};
`

export const MetricUnit = styled.span`
  ${Text('small', 'default')};
  align-self: center;
  margin: 0 ${spacing(1.2)} ${spacing(-4)} ${spacing(0.5)};
`

export const InputContainer = styled.div`
  display: flex;
  div {
    width: 100%;
    margin-bottom: 0;
    div {
      width: auto;
    }
  }

  ${(props) => mq(props.theme.breakpointUtils.map, 'sm')} {
    flex-basis: calc(50% - ${spacing(1)});
  }
`

export const DropDownGridItem = styled(GridItem)`
  margin-right: ${spacing(2.5)};
`

export const StyledInput = styled(Input)`
  div {
    margin-bottom: 0;
  }
`

export const DropdownGrid = styled(Grid)`
  margin-bottom: ${spacing(4)};
`

export const SubmitButtonsGrid = styled(Grid)`
  margin-bottom: ${spacing(6)};
`

export const AdditionalInfoTitle = styled.div`
  ${Text('medium1', 'default')};
`

export const LitresOfPaint = styled.div`
  ${Text('medium1', 'default')};
  margin-bottom: ${spacing(2)};
`

export const ShopNowButton = styled(Button)`
  margin-top: ${spacing(2)};
`

export const IconGridItem = styled(GridItem)`
  text-align: right;

  ${(props) => mq(props.theme.breakpointUtils.map, 'md')} {
    display: none;
  }
`

export const InstructionsButton = styled(Button)`
  :focus,
  :active {
    border-color: transparent;
  }
`
